import styled, { css, keyframes } from 'styled-components';

// Keyframes
const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const processingBar = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

const shimmer = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`;

// Status colors
const statusColors = {
  analysis: {
    main: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
    glow: 'rgba(139, 92, 246, 0.3)'
  },
  production: {
    main: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    glow: 'rgba(245, 158, 11, 0.3)'
  },
  ready: {
    main: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981, #34d399)',
    glow: 'rgba(16, 185, 129, 0.3)'
  }
};

export const CardWrapper = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 16px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  animation: ${cardEnter} 0.3s ease-out backwards;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: #318CD5;
  }

  @media (max-width: 1500px) {
    padding: 8px 10px;
    border-radius: 10px;
  }

  ${props => props.$isProcessing && css`
    opacity: 0.7;
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #318CD5, #10b981, #318CD5);
      background-size: 200% 100%;
      animation: ${processingBar} 1s linear infinite;
      border-radius: 14px 14px 0 0;
    }
  `}
`;

export const CancelledOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.92);
  color: white;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 3px;
  border-radius: 14px;
  backdrop-filter: blur(2px);
  z-index: 5;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 8px;

  @media (max-width: 1500px) {
    margin-bottom: 6px;
    gap: 6px;
  }
`;

export const OrderTypeBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;

  ${props => {
    if (props.$type === 'delivery') {
      return css`
        background: rgba(16, 185, 129, 0.12);
        color: #059669;
      `;
    }
    if (props.$type === 'pickup') {
      return css`
        background: rgba(245, 158, 11, 0.12);
        color: #d97706;
      `;
    }
    return css`
      background: rgba(139, 92, 246, 0.12);
      color: #7c3aed;
    `;
  }}

  @media (max-width: 1500px) {
    gap: 4px;
    padding: 3px 7px;
    font-size: 11px;
    border-radius: 7px;
  }
`;

export const OrderTypeIcon = styled.span`
  display: flex;
  font-size: 14px;

  @media (max-width: 1500px) {
    font-size: 12px;
  }
`;

export const OrderNumber = styled.span`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 15px;
  font-weight: 800;
  color: #1a1a2e;

  @media (max-width: 1500px) {
    font-size: 13px;
  }
`;

export const OrderTime = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: #f1f5f9;
  border-radius: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;

  @media (max-width: 1500px) {
    padding: 3px 7px;
    font-size: 11px;
    gap: 4px;
    border-radius: 6px;
  }
`;

export const TimeIcon = styled.span`
  display: flex;
  font-size: 12px;
  color: #318CD5;

  @media (max-width: 1500px) {
    font-size: 10px;
  }
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 1500px) {
    gap: 6px;
  }
`;

export const CustomerSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 1500px) {
    gap: 8px;
  }
`;

export const CustomerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`;

export const CustomerName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;

  @media (max-width: 1500px) {
    font-size: 13px;
    gap: 6px;
  }
`;

export const RecurrenceBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  background: ${props => props.$isFirst ? '#10b981' : '#94a3b8'};

  @media (max-width: 1500px) {
    width: 18px;
    height: 18px;
    font-size: 9px;
  }
`;

export const CustomerPhone = styled.div`
  font-size: 12px;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 1500px) {
    font-size: 11px;
  }
`;

export const TotalSection = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

export const TotalLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 1500px) {
    font-size: 9px;
    letter-spacing: 0.3px;
  }
`;

export const TotalValue = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 17px;
  font-weight: 800;
  color: #10b981;

  @media (max-width: 1500px) {
    font-size: 14px;
  }
`;

export const PaymentSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 10px;

  @media (max-width: 1500px) {
    gap: 6px;
    padding: 5px 8px;
    border-radius: 7px;
  }
`;

export const PaymentIcon = styled.span`
  display: flex;
  font-size: 16px;
  color: #94a3b8;

  @media (max-width: 1500px) {
    font-size: 14px;
  }
`;

export const PaymentMethod = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;

  @media (max-width: 1500px) {
    font-size: 11px;
  }
`;

export const DeliverySection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 10px;

  @media (max-width: 1500px) {
    gap: 6px;
    padding: 5px 8px;
    border-radius: 7px;
  }
`;

export const DeliveryIcon = styled.span`
  display: flex;
  font-size: 14px;
  color: #318CD5;
  flex-shrink: 0;

  @media (max-width: 1500px) {
    font-size: 12px;
  }
`;

export const DeliveryAddress = styled.div`
  font-size: 12px;
  color: #64748b;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 1500px) {
    font-size: 11px;
  }
`;

export const DriverButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: rgba(49, 140, 213, 0.08);
  border: 1px solid rgba(49, 140, 213, 0.15);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #318CD5;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;

  &:hover {
    background: rgba(49, 140, 213, 0.15);
    border-color: rgba(49, 140, 213, 0.25);
  }

  ${props => props.$placeholder && css`
    font-style: italic;
    color: #94a3b8;
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(0, 0, 0, 0.06);

    &:hover {
      color: #318CD5;
      background: rgba(49, 140, 213, 0.08);
      border-color: rgba(49, 140, 213, 0.15);
    }
  `}

  @media (max-width: 1500px) {
    padding: 4px 8px;
    font-size: 10px;
    border-radius: 6px;
    max-width: 100px;
    gap: 4px;
  }
`;

export const CardActions = styled.div`
  margin-top: 14px;

  @media (max-width: 1500px) {
    margin-top: 6px;
  }
`;

export const AdvanceButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  color: white;

  @media (max-width: 1500px) {
    height: 32px;
    font-size: 12px;
    border-radius: 8px;
    gap: 6px;
  }

  ${props => {
    const status = props.$status || 'analysis';
    const colors = statusColors[status];
    return css`
      background: ${colors.gradient};
      box-shadow: 0 4px 12px ${colors.glow};
    `;
  }}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${props => statusColors[props.$status || 'analysis'].glow};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;

    &:hover {
      transform: none;
    }
  }

  ${props => props.$status === 'production' && css`
    color: #1a1a2e;
  `}
`;

export const AdvanceIcon = styled.span`
  display: flex;
  font-size: 18px;
  transition: transform 0.2s ease;

  button:hover & {
    transform: translateX(3px);
  }

  @media (max-width: 1500px) {
    font-size: 15px;
  }
`;
